/**
 * Cleaner Availability Page
 * HTMX-driven filtering & pagination.
 * Retains accordion detail loading (JSON-based) and SearchPicker.
 */

import { SearchPicker } from '../components/SearchPicker.js';

let cleanerPicker = null;
let loadedDetails = {};

const state = {
  urls: {},
  translations: {},
};

/**
 * Initialize the availability page
 */
export function initAvailability(options = {}) {
  const { hasActiveFilters = false, urls = {}, translations = {} } = options;

  state.urls = urls;
  state.translations = translations;

  initFilterPanelUI(hasActiveFilters);
  initCleanerPicker(urls);
  setupRangeSelector();
  initDatepickerBridge();
  setupAccordionListeners();
}

/**
 * Filter panel toggle / reset (HTMX handles requests)
 */
function initFilterPanelUI(hasActiveFilters) {
  const panel = document.getElementById('filter-panel');
  const toggleBtn = document.getElementById('toggle-filters-btn');
  const resetBtn = document.getElementById('reset-filters-btn');
  const chevron = document.getElementById('filter-chevron');
  const form = document.getElementById('availabilityFilterForm');

  if (panel && hasActiveFilters) {
    panel.classList.remove('hidden');
    if (chevron) chevron.classList.add('rotate-180');
  }

  // Per-node idempotency flag via plain property — does not serialize
  // into HTMX hx-boost history snapshots.
  if (toggleBtn && panel && !toggleBtn._avFilterBound) {
    toggleBtn._avFilterBound = true;
    toggleBtn.addEventListener('click', () => {
      const isOpen = panel.classList.toggle('hidden');
      if (chevron) chevron.classList.toggle('rotate-180', !isOpen);
    });
  }

  if (resetBtn && form && !resetBtn._avResetBound) {
    resetBtn._avResetBound = true;
    resetBtn.addEventListener('click', () => {
      // Reset range to "today"
      const rangeSelect = form.querySelector('select[name="range"]');
      if (rangeSelect) {
        rangeSelect.value = 'today';
        const wrapper = rangeSelect.closest('.custom-select-wrapper');
        const textEl = wrapper?.querySelector('.custom-select-text');
        if (textEl && rangeSelect.options[0]) textEl.textContent = rangeSelect.options[0].text;
      }

      // Hide custom date fields
      const dateFromWrapper = document.getElementById('date-from-wrapper');
      const dateToWrapper = document.getElementById('date-to-wrapper');
      if (dateFromWrapper) dateFromWrapper.classList.add('hidden');
      if (dateToWrapper) dateToWrapper.classList.add('hidden');

      // Clear text/hidden inputs
      form.querySelectorAll('input[type="text"]').forEach((inp) => {
        inp.value = '';
      });
      form.querySelectorAll('input[type="hidden"]').forEach((inp) => {
        inp.value = '';
      });

      // Uncheck available-only toggle
      const availableToggle = document.getElementById('available-only-toggle');
      if (availableToggle) availableToggle.checked = false;

      // Clear cleaner picker
      if (cleanerPicker) cleanerPicker.clear();

      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  }
}

/**
 * Initialize cleaner search picker
 */
function initCleanerPicker(urls) {
  const cleanerInput = document.getElementById('cleaner-search');
  if (!cleanerInput) return;

  cleanerPicker = new SearchPicker({
    inputId: 'cleaner-search',
    hiddenInputId: 'cleaner-filter',
    dropdownId: 'cleaner-search-results',
    endpoint: urls.cleanerSearch || '/office/api/cleaners/search/',
    maxResults: 8,
    minChars: 0,
    renderItem: renderCleanerItem,
    onSelect: () => {
      const form = document.getElementById('availabilityFilterForm');
      if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
    onClear: () => {
      const form = document.getElementById('availabilityFilterForm');
      if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    },
  });
}

/**
 * Show/hide custom date fields based on range selector
 */
function setupRangeSelector() {
  const form = document.getElementById('availabilityFilterForm');
  const rangeSelect = form && form.querySelector('select[name="range"]');
  const dateFromWrapper = document.getElementById('date-from-wrapper');
  const dateToWrapper = document.getElementById('date-to-wrapper');

  if (!rangeSelect || rangeSelect._avRangeBound) return;
  rangeSelect._avRangeBound = true;

  rangeSelect.addEventListener('change', function () {
    if (this.value === 'custom') {
      if (dateFromWrapper) dateFromWrapper.classList.remove('hidden');
      if (dateToWrapper) dateToWrapper.classList.remove('hidden');
    } else {
      if (dateFromWrapper) dateFromWrapper.classList.add('hidden');
      if (dateToWrapper) dateToWrapper.classList.add('hidden');
    }
  });
}

/**
 * Bridge Flowbite datepicker changeDate → native change for HTMX
 */
function initDatepickerBridge() {
  document.querySelectorAll('[datepicker]').forEach((el) => {
    if (el._avDpBound) return;
    el._avDpBound = true;
    el.addEventListener('changeDate', () => {
      el.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });
}

/**
 * Setup accordion event listeners for lazy loading visit details.
 * Called on init and after each HTMX swap.
 *
 * Phase 14: also binds ``event.stopPropagation()`` on
 * ``[data-availability-stop]`` cells inside the accordion rows so a
 * click on the actions ``<td>`` (the calendar link) doesn't also
 * trigger Flowbite's accordion toggle on the parent ``<tr>``. Per-cell
 * idempotency flag (``_avStopBound``) keeps re-init after an HTMX swap
 * a no-op.
 */
export function setupAccordionListeners() {
  loadedDetails = {};
  document.querySelectorAll('[data-accordion-target]').forEach((row) => {
    if (row._avAccBound) return;
    row._avAccBound = true;
    row.addEventListener('click', function () {
      const targetId = this.getAttribute('data-accordion-target');
      const cleanerId = targetId.replace('#cleaner-body-', '');
      const detailContent = document.getElementById('detail-content-' + cleanerId);

      if (!loadedDetails[cleanerId] && detailContent) {
        loadVisitDetails(cleanerId, detailContent);
      }
    });
  });

  document.querySelectorAll('[data-availability-stop]').forEach((cell) => {
    if (cell._avStopBound) return;
    cell._avStopBound = true;
    cell.addEventListener('click', (event) => {
      event.stopPropagation();
    });
  });
}

/**
 * Load visit details for a cleaner (JSON-based, not HTMX)
 */
async function loadVisitDetails(cleanerId, container) {
  const params = new URLSearchParams(window.location.search);
  params.set('format', 'json');

  const detailUrl = state.urls.cleanerDetail
    ? state.urls.cleanerDetail.replace('{id}', cleanerId)
    : `/office/cleaners/availability/${cleanerId}/`;

  try {
    const response = await fetch(`${detailUrl}?${params.toString()}`);
    const data = await response.json();

    if (data.visits.length === 0) {
      container.innerHTML = `<div class="text-sm text-gray-500 dark:text-gray-400 py-2 italic">${state.translations.noVisits || 'No visits scheduled in this period.'}</div>`;
    } else {
      let html = `
        <table class="w-full text-sm text-left no-row-hover">
          <thead class="text-xs text-gray-500 dark:text-gray-400 uppercase bg-gray-100 dark:bg-gray-700">
            <tr>
              <th class="px-3 py-2 rounded-l-lg">${state.translations.dateTime || 'Date/Time'}</th>
              <th class="px-3 py-2">${state.translations.customer || 'Customer'}</th>
              <th class="px-3 py-2">${state.translations.location || 'Location'}</th>
              <th class="px-3 py-2">${state.translations.duration || 'Duration'}</th>
              <th class="px-3 py-2 rounded-r-lg">${state.translations.status || 'Status'}</th>
            </tr>
          </thead>
          <tbody>
      `;

      for (const visit of data.visits) {
        const start = new Date(visit.scheduled_start);
        const end = visit.scheduled_end ? new Date(visit.scheduled_end) : null;
        const startTime = start.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        const endTime = end
          ? end.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
          : '';
        const locale = document.documentElement.lang || 'en';
        const monthRaw = start.toLocaleDateString(locale, { month: 'short' });
        const monthName = monthRaw.charAt(0).toUpperCase() + monthRaw.slice(1);
        const dateStr = `${start.getDate()} ${monthName} ${start.getFullYear()}`;
        const timeRange = endTime ? `${startTime} - ${endTime}` : startTime;
        const duration = Math.round((visit.duration_minutes / 60) * 10) / 10;

        html += `
          <tr class="border-b border-gray-200 dark:border-gray-700 cursor-pointer"
              onmouseenter="this.style.setProperty('background-color','#f6f7f7','important');this.querySelectorAll('td').forEach(function(td){td.style.setProperty('border-color','transparent','important')});var p=this.previousElementSibling;if(p)p.querySelectorAll('td').forEach(function(td){td.style.setProperty('border-color','transparent','important')})"
              onmouseleave="this.style.removeProperty('background-color');this.querySelectorAll('td').forEach(function(td){td.style.removeProperty('border-color')});var p=this.previousElementSibling;if(p)p.querySelectorAll('td').forEach(function(td){td.style.removeProperty('border-color')})"
              onclick="event.stopPropagation(); if(window.showVisitDrawer) window.showVisitDrawer(${visit.id});">
            <td class="px-3 py-2.5 whitespace-nowrap">
              <div class="font-medium text-gray-900 dark:text-white">${dateStr}</div>
              <div class="text-xs text-gray-500 dark:text-gray-400">${timeRange}</div>
            </td>
            <td class="px-3 py-2.5 text-gray-900 dark:text-white">${escapeHtml(visit.customer_name)}</td>
            <td class="px-3 py-2.5 text-gray-500 dark:text-gray-400">${escapeHtml(visit.location_label)}</td>
            <td class="px-3 py-2.5 text-gray-900 dark:text-white">${duration}h</td>
            <td class="px-3 py-2.5"><span class="px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(visit.status)}">${escapeHtml(visit.status_display)}</span></td>
          </tr>
        `;
      }

      html += '</tbody></table>';
      container.innerHTML = html;
    }

    loadedDetails[cleanerId] = true;
  } catch (error) {
    console.error('Error loading visit details:', error);
    container.innerHTML = `<div class="text-sm text-red-500 py-2">${state.translations.errorLoading || 'Error loading visits. Please try again.'}</div>`;
  }
}

function getStatusBadgeClass(status) {
  switch (status) {
    case 'scheduled':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    case 'in_progress':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
    case 'done':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    case 'cancelled':
    case 'no_show':
      return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
}

function renderCleanerItem(item) {
  const name = item.name || item.display_name;
  const initials = item.initials || getInitials(name);
  const photo = item.photo_url || item.avatar_url;

  if (photo) {
    return `
      <div class="flex items-center px-3 py-2">
        <img src="${photo}" alt="" class="w-7 h-7 rounded-full object-cover mr-2.5 flex-shrink-0">
        <span class="text-sm font-medium text-gray-900 dark:text-white">${escapeHtml(name)}</span>
      </div>
    `;
  }

  return `
    <div class="flex items-center px-3 py-2">
      <div class="w-7 h-7 rounded-full flex items-center justify-center mr-2.5 flex-shrink-0 bg-primary-600">
        <span class="text-xs font-medium text-white">${initials}</span>
      </div>
      <span class="text-sm font-medium text-gray-900 dark:text-white">${escapeHtml(name)}</span>
    </div>
  `;
}

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

let afterSettleBound = false;

function bindAfterSettleOnce() {
  if (afterSettleBound) return;
  afterSettleBound = true;
  document.addEventListener('htmx:afterSettle', function () {
    if (typeof window.reinitFlowbiteWidgets === 'function') {
      window.reinitFlowbiteWidgets();
    }
    setupAccordionListeners();
  });
}

/**
 * Auto-init entry point (wired via ``data-auto-init="availability"``).
 */
export function autoInitAvailability() {
  const root = document.querySelector('[data-auto-init="availability"]');
  if (!root) return;
  const ds = root.dataset;
  initAvailability({
    hasActiveFilters: ds.hasActiveFilters === '1',
    urls: {
      cleanerSearch: ds.cleanerSearchUrl || '/office/api/cleaners/search/',
      cleanerDetail: ds.cleanerDetailUrl || '/office/cleaners/availability/{id}/',
    },
    translations: {
      noVisits: ds.trNoVisits || 'No visits scheduled in this period.',
      dateTime: ds.trDateTime || 'Date/Time',
      customer: ds.trCustomer || 'Customer',
      location: ds.trLocation || 'Location',
      duration: ds.trDuration || 'Duration',
      status: ds.trStatus || 'Status',
      errorLoading: ds.trErrorLoading || 'Error loading visits. Please try again.',
    },
  });
  bindAfterSettleOnce();
}

window.initAvailability = initAvailability;
